/**
 * Navigation Component
 * ====================
 * Main navigation bar with responsive mobile menu.
 * Shows different links based on authentication state.
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, LogOut, LayoutDashboard, Play } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { NAV_LINKS, AUTH_CONFIG } from '../../utils/constants';

/**
 * Navigation Component
 * Responsive navigation with mobile menu support.
 * 
 * @returns {JSX.Element} Navigation component
 */
export function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  
  // State
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);
  
  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  // Check if link is active
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };
  
  // Get visible nav links based on auth state
  const getNavLinks = () => {
    const links = [...NAV_LINKS.public];
    
    if (isAuthenticated) {
      links.push(...NAV_LINKS.protected);
    }
    
    return links;
  };
  
  const navLinks = getNavLinks();
  
  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-md py-3'
            : 'bg-transparent py-5'
        }`}
      >
        <div className="container-dme">
          <nav className="flex items-center justify-between">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-3 group"
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${
                isScrolled 
                  ? 'bg-gradient-to-br from-dme-blue to-dme-blue-dark' 
                  : 'bg-white/20 backdrop-blur-sm'
              }`}>
                <span className="text-white font-bold text-lg">D</span>
              </div>
              <span className={`font-heading font-bold text-xl transition-colors duration-300 ${
                isScrolled ? 'text-dme-blue-800' : 'text-white'
              }`}>
                DME-CPH
              </span>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive(link.path)
                      ? isScrolled
                        ? 'text-dme-orange bg-dme-orange-50'
                        : 'text-white bg-white/20'
                      : isScrolled
                        ? 'text-dme-gray-600 hover:text-dme-blue hover:bg-dme-gray-50'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            
            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center gap-3">
              {isAuthenticated ? (
                <>
                  <a
                    href="https://addi.vaultailab.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isScrolled
                        ? 'text-dme-blue hover:bg-dme-blue-50'
                        : 'text-white hover:bg-white/10'
                    }`}
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard
                  </a>
                  <button
                    onClick={handleLogout}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isScrolled
                        ? 'text-dme-gray-600 hover:text-red-600 hover:bg-red-50'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to={AUTH_CONFIG.LOGIN_PATH}
                  className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    isScrolled
                      ? 'bg-dme-orange text-white hover:bg-dme-orange-dark hover:shadow-orange'
                      : 'bg-white text-dme-blue hover:bg-dme-orange hover:text-white'
                  }`}
                >
                  Sign In
                </Link>
              )}
            </div>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${
                isScrolled
                  ? 'text-dme-gray-700 hover:bg-dme-gray-100'
                  : 'text-white hover:bg-white/10'
              }`}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </nav>
        </div>
      </header>
      
      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          isMobileMenuOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
        
        {/* Menu Panel */}
        <div
          className={`absolute top-0 right-0 w-full max-w-sm h-full bg-white shadow-2xl transition-transform duration-300 ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Mobile Header */}
            <div className="flex items-center justify-between p-4 border-b border-dme-gray-100">
              <Link to="/" className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-dme-blue to-dme-blue-dark flex items-center justify-center">
                  <span className="text-white font-bold text-lg">D</span>
                </div>
                <span className="font-heading font-bold text-xl text-dme-blue-800">
                  DME-CPH
                </span>
              </Link>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-lg text-dme-gray-500 hover:bg-dme-gray-100"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Mobile Links */}
            <div className="flex-1 overflow-y-auto py-4">
              <div className="px-4 space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      isActive(link.path)
                        ? 'text-dme-orange bg-dme-orange-50'
                        : 'text-dme-gray-700 hover:text-dme-blue hover:bg-dme-blue-50'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
              
              {/* Divider */}
              <div className="my-4 border-t border-dme-gray-100" />
              
              {/* Mobile Actions */}
              <div className="px-4 space-y-2">
                {isAuthenticated ? (
                  <>
                    <a
                      href="https://addi.vaultailab.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-dme-blue hover:bg-dme-blue-50"
                    >
                      <LayoutDashboard className="w-5 h-5" />
                      Dashboard
                    </a>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="w-5 h-5" />
                      Logout
                    </button>
                  </>
                ) : (
                  <Link
                    to={AUTH_CONFIG.LOGIN_PATH}
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-dme-orange text-white rounded-lg font-semibold hover:bg-dme-orange-dark"
                  >
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Navigation;
